import {
  GoalLifeAreaLinkChange,
  GoalParentLinkChange,
  GoalSortingMode,
  GroupByMode,
  OrderDirection,
} from '@griply/types';
import {
  filterGoals,
  filterGoalsByFilterDocument,
  filterLocalizedLifeAreas,
  getGoalListOrder,
  getLifeAreaGoalFilterDocument,
  getWeekStartsOn,
} from '@griply/utils';
import {
  useMergeGoalLinkChanges,
  useSortedGoals,
  useTimelineDropReorder,
  useUpdateGoal,
} from 'features/goals';
import {
  useSortedLocalizedLifeAreas,
  useUpdateLifeArea,
} from 'features/life-areas';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalTimelineContainer } from 'shared/components/ui/timeline';
import { useToday } from 'shared/contexts/today';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useUser } from 'shared/hooks/use-user';
import { GoalReorderSectionUpdate, UpdateGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import {
  TimelineScrollRequest,
  TimelineTimeframes,
} from 'shared/types/timeline';
import { Timestamp } from 'shared/types/timestamp';

import * as Styled from './life-area-roadmap-timeline.style';
import { resolveLifeAreaTimelineReorder } from './resolve-life-area-timeline-reorder';

export type LifeAreaRoadmapTimelineProps = {
  lifeArea: LifeArea;
  timeframe: TimelineTimeframes;
  referenceDate: Timestamp;
  scrollRequest: TimelineScrollRequest;
  hideCompleted?: boolean;
};

export const LifeAreaRoadmapTimeline: React.FC<
  LifeAreaRoadmapTimelineProps
> = ({ lifeArea, timeframe, referenceDate, scrollRequest, hideCompleted }) => {
  const { t } = useTranslation();
  const user = useUser();
  const today = useToday();
  const weekStartsOn = getWeekStartsOn(user);
  const allGoals = useSortedGoals();
  const links = useEntityLinks();
  const allLifeAreas = useSortedLocalizedLifeAreas();
  const { submit: updateGoal } = useUpdateGoal();
  const { submit: updateLifeArea } = useUpdateLifeArea();

  const filterDocument = useMemo(
    () => getLifeAreaGoalFilterDocument(lifeArea, allGoals),
    [lifeArea, allGoals],
  );

  // Archived items never show on a timeline; completed follow the toggle.
  const goals = useMemo(() => {
    const base = filterGoals(allGoals, {
      links,
      hideArchived: true,
      hideCompleted: !!hideCompleted,
    });
    return filterGoalsByFilterDocument(base, filterDocument, {
      links,
      today,
      weekStartsOn,
    });
  }, [allGoals, filterDocument, hideCompleted, links, today, weekStartsOn]);

  const lifeAreas = useMemo(
    () =>
      filterLocalizedLifeAreas(allLifeAreas, {
        goals,
        showEmptyLifeAreas: false,
        allGoals,
        links,
      }),
    [allLifeAreas, goals, allGoals, links],
  );

  const customOrder = useMemo(
    () =>
      getGoalListOrder({
        goals,
        allGoals,
        sortMode: lifeArea.goalSortingMode ?? GoalSortingMode.Custom,
        sortDirection: user?.settings?.goalSortDirection ?? OrderDirection.Asc,
        goalSorting: lifeArea.goalSorting,
        links,
      }),
    [goals, allGoals, lifeArea, user, links],
  );

  const handleChangeGoal = useCallback(
    (id: ID, { startDate, deadline }: Partial<UpdateGoal>) => {
      const update: UpdateGoal = { id };
      if (startDate) {
        update.startDate = startDate;
      }
      if (deadline) {
        update.deadline = deadline;
      }
      updateGoal(update);
    },
    [updateGoal],
  );

  const mergeGoalLinkChanges = useMergeGoalLinkChanges();

  const handleReorder = useCallback(
    (
      updatedSections: GoalReorderSectionUpdate[],
      updatedGoals: UpdateGoal[],
      goalLifeAreaChanges: GoalLifeAreaLinkChange[] = [],
      goalParentChanges: GoalParentLinkChange[] = [],
    ) => {
      const { goalUpdates, lifeAreaGoalSorting } =
        resolveLifeAreaTimelineReorder(updatedSections, updatedGoals);

      // This timeline's root is the life area: a goal dropped there with no parent left over
      // links to the area instead. A goal that kept a parent is left alone.
      const reattached = goalParentChanges
        .filter(({ parentIds }) => !parentIds.length)
        .map(({ goalId }) => ({ goalId, lifeAreaIds: [lifeArea.id] }));

      mergeGoalLinkChanges(goalUpdates, {
        goalLifeAreaChanges: [...goalLifeAreaChanges, ...reattached],
        goalParentChanges,
      }).forEach((update) => updateGoal(update));

      if (lifeAreaGoalSorting) {
        updateLifeArea({
          id: lifeArea.id,
          goalSorting: lifeAreaGoalSorting,
          goalSortingMode: GoalSortingMode.Custom,
        });
      }
    },
    [lifeArea.id, updateGoal, updateLifeArea, mergeGoalLinkChanges],
  );

  const onReorderDrop = useTimelineDropReorder(handleReorder);

  const handleChangeCollapsedItems = useCallback(
    (ids: ID[]) => updateLifeArea({ id: lifeArea.id, collapsedGoals: ids }),
    [lifeArea.id, updateLifeArea],
  );

  return (
    <Styled.Container>
      <GoalTimelineContainer
        links={links}
        timeframe={timeframe}
        referenceDate={referenceDate}
        scrollRequest={scrollRequest}
        goals={goals}
        allGoals={allGoals}
        lifeAreas={lifeAreas}
        weekStartsOn={weekStartsOn}
        groupBy={GroupByMode.None}
        customOrder={customOrder}
        onChangeGoal={handleChangeGoal}
        onReorderDrop={onReorderDrop}
        onChangeCollapsedItems={handleChangeCollapsedItems}
        collapsedItems={lifeArea.collapsedGoals ?? undefined}
        title={t('pages.life-area-detail.tabs.roadmap.label')}
      />
    </Styled.Container>
  );
};
